/**
 * ============================================
 * UNIFIED THEME CONFIGURATION
 * ============================================
 * 
 * This file consolidates ALL visual styling (colors, typography, spacing) 
 * into a single source of truth for the entire application.
 * 
 * 🎨 QUICK REFERENCE - What Changes When You Update Colors:
 * 
 * primary (#0B7EB5 - Monarch Blue):
 *   → Primary buttons, links, charts, pagination, Recent Orders header
 * 
 * secondary (#1B2A3D - Monarch Navy):
 *   → Dashboard card headers, DataGrid headers, table headers
 * 
 * headerBg (#1B2A3D - Monarch Navy):
 *   → Top navigation bar
 * 
 * sidebarBg (#0F1923 - Dark Navy):
 *   → Left sidebar navigation
 * 
 * textPrimary (#1B2A3D - Monarch Navy):
 *   → Main body text, table content, form labels
 * 
 * textStrong (#0B7EB5 - Monarch Blue):
 *   → Headings, login page gradient, button colors
 * 
 * 
 * 📝 HOW TO USE:
 * 1. Change color values in THEME.colors below
 * 2. IMPORTANT: Also update DEFAULT_COLORS in public/index.html to match for preloading!
 * 3. Save the file
 * 4. All components update automatically (no code changes needed!)
 * 
 * 🔧 TECHNICAL DETAILS:
 * - Colors are injected as CSS variables (--admin-primary, etc.)
 * - MUI components use these colors via muiTheme.js
 * - Inline styles are replaced by MutationObserver
 * - React components access via useTheme() hook
 * 
 * @typedef {import('../types/theme').ThemeConfig} ThemeConfig
 * @typedef {import('../types/theme').ThemeVars} ThemeVars
 */

// Helper function to convert hex to RGB
const hexToRgb = (hex) => {
  const normalized = hex.replace("#", "");
  const fullHex =
    normalized.length === 3
      ? normalized.split("").map((v) => `${v}${v}`).join("")
      : normalized;
  const bigint = parseInt(fullHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
};

/**
 * Main theme configuration object
 * 
 * ============================================
 * IMPORTANT: SINGLE SOURCE OF TRUTH FOR COLORS
 * ============================================
 * 
 * This is the ONLY place where you need to change colors.
 * All changes here automatically update the entire application including:
 * - MUI components (buttons, inputs, tables, DataGrid, etc.)
 * - Dashboard headers and cards
 * - Login and forgot password page backgrounds
 * - Sidebar and header navigation
 * - All inline styles using CSS variables
 * 
 * @type {ThemeConfig}
 */
export const THEME = {
  colors: {
    // ============================================
    // PRIMARY BRAND COLORS
    // ============================================
    
    /**
     * primary - Main brand color (Green #249B47)
     * 
     * WHERE IT'S USED:
     * - Primary buttons and CTAs
     * - Links and interactive elements
     * - Chart lines and data visualization
     * - MUI primary color palette
     * - Pagination selected state
     * - Success indicators
     * - Recent Orders table header background
     * - Hover states on buttons
     */
    primary: "#0B7EB5",
    
    /**
     * secondary - Secondary brand color (Dark Blue #143D60)
     * 
     * WHERE IT'S USED:
     * - Dashboard card headers (Order Traffic, Sales Statistics, Recent Orders, Activity Logs)
     * - MUI DataGrid column headers background
     * - MUI Table headers background
     * - Secondary buttons
     * - Accent elements
     */
    secondary: "#1B2A3D",
    
    // ============================================
    // UI ELEMENT COLORS
    // ============================================
    
    /**
     * headerBg - Top navigation header background (#344955)
     * 
     * WHERE IT'S USED:
     * - Main application header/navbar at the top
     * - MUI AppBar component
     */
    headerBg: "#1B2A3D",
    
    /**
     * sidebarBg - Left sidebar navigation background (#292D32)
     * 
     * WHERE IT'S USED:
     * - Main sidebar navigation on the left
     * - MUI Drawer component
     */
    sidebarBg: "#0F1923",
    
    // ============================================
    // TEXT COLORS
    // ============================================
    
    /**
     * textPrimary - Primary text color (Dark Gray #344955)
     * 
     * WHERE IT'S USED:
     * - Main body text throughout the app
     * - Table cell content
     * - Form labels
     * - Card content text
     * - Login/Forgot Password page gradient (end color)
     * - Dashboard table text
     */
    textPrimary: "#1B2A3D",
    
    /**
     * textSecondary - Secondary/muted text color (#6c757d)
     * 
     * WHERE IT'S USED:
     * - Helper text and descriptions
     * - Placeholder text in inputs
     * - Disabled text
     * - Timestamps and metadata
     * - Email addresses in tables
     */
    textSecondary: "#6c757d",
    
    /**
     * textStrong - Strong emphasis text color (#3c4b64)
     * 
     * WHERE IT'S USED:
     * - Headings and titles
     * - Important labels
     * - Login/Forgot Password page gradient (start color)
     * - Login/Forgot Password button background
     * - Emphasized text
     */
    textStrong: "#0B7EB5",
    
    /**
     * textLight - Light/subtle text color (#8a93a2)
     * 
     * WHERE IT'S USED:
     * - Disabled states
     * - Very subtle text
     * - Watermarks
     * - Less important information
     */
    textLight: "#8a93a2",
    
    // ============================================
    // BACKGROUND COLORS
    // ============================================
    
    /**
     * background - Main page background (#f8f9fa)
     * 
     * WHERE IT'S USED:
     * - Main page backgrounds
     * - Content area backgrounds
     * - DataGrid footer background
     */
    background: "#f8f9fa",
    
    /**
     * backgroundAlt - Alternative/elevated background (#ffffff)
     * 
     * WHERE IT'S USED:
     * - Cards and panels
     * - Modals and dialogs
     * - Elevated surfaces
     * - Login/Forgot Password card background
     */
    backgroundAlt: "#ffffff",
    
    // ============================================
    // BORDER COLORS
    // ============================================
    
    /**
     * border - Standard border color (#d8dbe0)
     * 
     * WHERE IT'S USED:
     * - Card borders
     * - Input borders (default state)
     * - Dividers
     * - Table borders
     * - DataGrid borders
     */
    border: "#d8dbe0",
    
    /**
     * borderLight - Subtle border color (#e9ecef)
     * 
     * WHERE IT'S USED:
     * - Light dividers
     * - Subtle separators
     * - Table cell borders
     * - DataGrid cell borders
     */
    borderLight: "#e9ecef",
    
    // ============================================
    // SEMANTIC COLORS
    // ============================================
    
    /**
     * success - Success state color (#28a745)
     * 
     * WHERE IT'S USED:
     * - Success messages and alerts
     * - Positive indicators
     * - Completed states
     * - Success chips/badges
     * - "Paid" status indicators
     */
    success: "#28a745",
    
    /**
     * danger - Error/danger state color (#dc3545)
     * 
     * WHERE IT'S USED:
     * - Error messages and alerts
     * - Delete buttons
     * - Warning indicators
     * - Validation errors
     * - Failed states
     */
    danger: "#dc3545",
    
    /**
     * warning - Warning state color (#ffc107)
     * 
     * WHERE IT'S USED:
     * - Warning messages and alerts
     * - Pending states
     * - Caution indicators
     * - "Pending" status chips
     */
    warning: "#ffc107",
    
    /**
     * info - Information state color (#17a2b8)
     * 
     * WHERE IT'S USED:
     * - Info messages and alerts
     * - Informational badges
     * - Help indicators
     * - Neutral status indicators
     */
    info: "#17a2b8",
    
    // ============================================
    // NEUTRAL COLORS
    // ============================================
    
    /**
     * white - Pure white (#ffffff)
     * 
     * WHERE IT'S USED:
     * - Text on dark backgrounds (headers, buttons)
     * - Card backgrounds
     * - Button text on colored backgrounds
     * - Header text on colored backgrounds
     * - DataGrid header text
     */
    white: "#ffffff",
    
    /**
     * black - Pure black (#000000)
     * 
     * WHERE IT'S USED:
     * - High contrast text (rarely used)
     * - Icons
     * - Overlays
     */
    black: "#000000",
    
    /**
     * gray - Neutral gray (#6c757d)
     * 
     * WHERE IT'S USED:
     * - Neutral elements
     * - Disabled states
     * - Subtle backgrounds
     * - Switch track (unchecked)
     */
    gray: "#6c757d",
  },
  
  typography: {
    fontFamily: "PT Sans, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSizeBase: "16px",
    fontSizeSmall: "14px",
    fontSizeLarge: "18px",
    fontWeightNormal: "400",
    fontWeightBold: "700",
  },
  
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "16px",
    lg: "24px",
    xl: "32px",
  },
  
  borderRadius: {
    sm: "4px",
    md: "8px",
    lg: "12px",
  },
};

/**
 * CSS Variable references for use in JavaScript
 * @type {ThemeVars}
 */
export const THEME_VARS = {
  colors: {
    primary: "var(--admin-primary)",
    primaryRgb: "var(--admin-primary-rgb)",
    secondary: "var(--admin-secondary)",
    secondaryRgb: "var(--admin-secondary-rgb)",
    headerBg: "var(--admin-header-bg)",
    headerBgRgb: "var(--admin-header-bg-rgb)",
    sidebarBg: "var(--admin-sidebar-bg)",
    sidebarBgRgb: "var(--admin-sidebar-bg-rgb)",
    textPrimary: "var(--admin-text-primary)",
    textPrimaryRgb: "var(--admin-text-primary-rgb)",
    textSecondary: "var(--admin-text-secondary)",
    textSecondaryRgb: "var(--admin-text-secondary-rgb)",
    textStrong: "var(--admin-text-strong)",
    textStrongRgb: "var(--admin-text-strong-rgb)",
    textLight: "var(--admin-text-light)",
    textLightRgb: "var(--admin-text-light-rgb)",
    background: "var(--admin-background)",
    backgroundRgb: "var(--admin-background-rgb)",
    backgroundAlt: "var(--admin-background-alt)",
    backgroundAltRgb: "var(--admin-background-alt-rgb)",
    border: "var(--admin-border)",
    borderRgb: "var(--admin-border-rgb)",
    borderLight: "var(--admin-border-light)",
    borderLightRgb: "var(--admin-border-light-rgb)",
    success: "var(--admin-success)",
    successRgb: "var(--admin-success-rgb)",
    danger: "var(--admin-danger)",
    dangerRgb: "var(--admin-danger-rgb)",
    warning: "var(--admin-warning)",
    warningRgb: "var(--admin-warning-rgb)",
    info: "var(--admin-info)",
    infoRgb: "var(--admin-info-rgb)",
    white: "var(--admin-white)",
    whiteRgb: "var(--admin-white-rgb)",
    black: "var(--admin-black)",
    blackRgb: "var(--admin-black-rgb)",
    gray: "var(--admin-gray)",
    grayRgb: "var(--admin-gray-rgb)",
  },
  typography: {
    fontFamily: "var(--admin-font-family)",
    fontSizeBase: "var(--admin-font-size-base)",
    fontSizeSmall: "var(--admin-font-size-small)",
    fontSizeLarge: "var(--admin-font-size-large)",
    fontWeightNormal: "var(--admin-font-weight-normal)",
    fontWeightBold: "var(--admin-font-weight-bold)",
  },
  spacing: {
    xs: "var(--admin-spacing-xs)",
    sm: "var(--admin-spacing-sm)",
    md: "var(--admin-spacing-md)",
    lg: "var(--admin-spacing-lg)",
    xl: "var(--admin-spacing-xl)",
  },
  borderRadius: {
    sm: "var(--admin-border-radius-sm)",
    md: "var(--admin-border-radius-md)",
    lg: "var(--admin-border-radius-lg)",
  },
};

/**
 * Helper function to create rgba from CSS variable
 * @param {string} rgbVar - RGB CSS variable value (e.g., "255, 0, 0")
 * @param {number} alpha - Alpha value (0-1)
 * @returns {string} RGBA color string
 */
export const rgbaFromVar = (rgbVar, alpha) => `rgba(${rgbVar}, ${alpha})`;

/**
 * Apply theme variables to document root
 * Injects all theme values as CSS custom properties on document.documentElement
 * 
 * @param {ThemeConfig} [theme=THEME] - Theme configuration to apply
 * @returns {void}
 * 
 * @example
 * // Apply default theme
 * applyThemeVariables();
 * 
 * @example
 * // Apply custom theme
 * applyThemeVariables(customTheme);
 */
export const applyThemeVariables = (theme = THEME) => {
  if (typeof document === "undefined") return;
  
  const root = document.documentElement;
  
  // Apply color variables
  Object.entries(theme.colors).forEach(([key, value]) => {
    const cssVarName = `--admin-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`;
    root.style.setProperty(cssVarName, value);
    root.style.setProperty(`${cssVarName}-rgb`, hexToRgb(value));
  });
  
  // Apply typography variables
  Object.entries(theme.typography).forEach(([key, value]) => {
    const cssVarName = `--admin-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`;
    root.style.setProperty(cssVarName, value);
  });
  
  // Apply spacing variables
  Object.entries(theme.spacing).forEach(([key, value]) => {
    const cssVarName = `--admin-spacing-${key}`;
    root.style.setProperty(cssVarName, value);
  });
  
  // Apply border radius variables
  Object.entries(theme.borderRadius).forEach(([key, value]) => {
    const cssVarName = `--admin-border-radius-${key}`;
    root.style.setProperty(cssVarName, value);
  });
};

// Inline style replacement map
const createReplacementMap = () => {
  const map = {};
  
  // Map old hardcoded colors to new CSS variables
  const colorMappings = {
    // Old green primary
    "#249B47": THEME_VARS.colors.primary,
    // New Monarch blue primary
    "#0B7EB5": THEME_VARS.colors.primary,
    // Success color
    "#28a745": THEME_VARS.colors.success,
    // Old secondary
    "#143D60": THEME_VARS.colors.secondary,
    // New Monarch navy secondary
    "#1B2A3D": THEME_VARS.colors.secondary,
    // Warning colors
    "#EB5B00": THEME_VARS.colors.warning,
    "#ffc107": THEME_VARS.colors.warning,
    // Info colors
    "#608BC1": THEME_VARS.colors.info,
    "#17a2b8": THEME_VARS.colors.info,
    // Old text primary
    "#344955": THEME_VARS.colors.textPrimary,
    // Old text strong
    "#3c4b64": THEME_VARS.colors.textStrong,
    // Old sidebar
    "#292D32": THEME_VARS.colors.sidebarBg,
    // New sidebar
    "#0F1923": THEME_VARS.colors.sidebarBg,
    // Text secondary
    "#6c757d": THEME_VARS.colors.textSecondary,
    // Background
    "#f8f9fa": THEME_VARS.colors.background,
    // White
    "#ffffff": THEME_VARS.colors.white,
    // Danger
    "#dc3545": THEME_VARS.colors.danger,
    // Old color that might be in code
    "#192231": THEME_VARS.colors.secondary,
  };
  
  // Add hex and rgb variants
  Object.entries(colorMappings).forEach(([hex, cssVar]) => {
    map[hex] = cssVar;
    map[hex.toLowerCase()] = cssVar;
    
    // Add RGB variants
    const rgb = hexToRgb(hex);
    map[`rgb(${rgb})`] = cssVar;
    map[`RGB(${rgb})`] = cssVar;
  });
  
  return map;
};

const inlineReplacementMap = createReplacementMap();

// Escape special regex characters
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Replace inline styles in a single element
const replaceInlineStyles = (element) => {
  if (!element || element.nodeType !== 1) return;
  
  const styleText = element.getAttribute("style");
  if (!styleText) return;
  
  let updated = styleText;
  Object.entries(inlineReplacementMap).forEach(([from, to]) => {
    const regex = new RegExp(escapeRegExp(from), "gi");
    updated = updated.replace(regex, to);
  });
  
  if (updated !== styleText) {
    element.setAttribute("style", updated);
  }
};

// Replace inline styles in element tree
const replaceInlineStylesInTree = (root) => {
  if (!root || root.nodeType !== 1) return;
  replaceInlineStyles(root);
  root.querySelectorAll?.("[style]").forEach(replaceInlineStyles);
};
// Debounce helper for MutationObserver - using a smaller delay for better responsiveness
let debounceTimer;
const debounceMutations = (callback, delay = 16) => {
  return (...args) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => callback(...args), delay);
  };
};

/**
 * Apply inline theme replacements with DOM observation
 * Replaces hardcoded color values in inline styles with CSS variables
 * Monitors DOM for dynamically added elements and applies replacements
 * 
 * @returns {void}
 * 
 * @example
 * // Initialize inline style replacement system
 * applyInlineThemeReplacements();
 */
export const applyInlineThemeReplacements = () => {
  if (typeof document === "undefined") return;

  // Initial replacement
  replaceInlineStylesInTree(document.documentElement);

  // Debounced mutation handler for performance
  const handleMutations = debounceMutations((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === "attributes") {
        replaceInlineStyles(mutation.target);
        return;
      }
      mutation.addedNodes?.forEach((node) => replaceInlineStylesInTree(node));
    });
  }, 50);

  // Observe DOM changes
  const observer = new MutationObserver(handleMutations);

  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["style"],
  });
};

/**
 * Update theme at runtime
 * Supports both full theme replacement and partial updates
 * 
 * @param {ThemeConfig} newTheme - New theme configuration (can be partial)
 * @param {boolean} [merge=false] - If true, merge with existing theme; if false, replace completely
 * @returns {void}
 * 
 * @example
 * // Full theme update
 * updateTheme(newThemeConfig);
 * 
 * // Partial update (merge with existing)
 * updateTheme({ colors: { primary: "#FF0000" } }, true);
 */
export const updateTheme = (newTheme, merge = false) => {
  let themeToApply = newTheme;
  
  // If merge is true, merge with existing theme
  if (merge && window.__CURRENT_THEME__) {
    themeToApply = {
      ...window.__CURRENT_THEME__,
      colors: { ...window.__CURRENT_THEME__.colors, ...newTheme.colors },
      typography: { ...window.__CURRENT_THEME__.typography, ...newTheme.typography },
      spacing: { ...window.__CURRENT_THEME__.spacing, ...newTheme.spacing },
      borderRadius: { ...window.__CURRENT_THEME__.borderRadius, ...newTheme.borderRadius },
    };
  }
  
  // Store current theme globally for partial updates
  window.__CURRENT_THEME__ = themeToApply;
  
  applyThemeVariables(themeToApply);
  
  // Trigger re-render of components using theme context
  if (window.__THEME_UPDATE_CALLBACK__) {
    window.__THEME_UPDATE_CALLBACK__(themeToApply);
  }
};

/**
 * Load theme from localStorage
 * 
 * @param {string} [key='admin_theme'] - localStorage key (defaults to 'admin_theme')
 * @returns {ThemeConfig|null} Theme configuration or null if not found
 * 
 * @example
 * const savedTheme = loadThemeFromStorage();
 * if (savedTheme) {
 *   updateTheme(savedTheme);
 * }
 */
export const loadThemeFromStorage = (key = 'admin_theme') => {
  if (typeof localStorage === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    
    const theme = JSON.parse(stored);
    
    // Validate before returning
    if (validateTheme(theme)) {
      return theme;
    } else {
      console.warn('Invalid theme in localStorage, ignoring');
      return null;
    }
  } catch (error) {
    console.error('Error loading theme from localStorage:', error);
    return null;
  }
};

/**
 * Save theme to localStorage
 * 
 * @param {ThemeConfig} theme - Theme configuration to save
 * @param {string} [key='admin_theme'] - localStorage key (defaults to 'admin_theme')
 * @returns {boolean} True if saved successfully
 * 
 * @example
 * saveThemeToStorage(customTheme);
 */
export const saveThemeToStorage = (theme, key = 'admin_theme') => {
  if (typeof localStorage === 'undefined') return false;
  
  try {
    // Validate before saving
    if (!validateTheme(theme)) {
      console.error('Cannot save invalid theme to localStorage');
      return false;
    }
    
    localStorage.setItem(key, JSON.stringify(theme));
    return true;
  } catch (error) {
    console.error('Error saving theme to localStorage:', error);
    return false;
  }
};

/**
 * Load theme from external API
 * 
 * @param {string} url - API endpoint URL
 * @returns {Promise<ThemeConfig|null>} Theme configuration or null if failed
 * 
 * @example
 * const theme = await loadThemeFromAPI('/api/theme');
 * if (theme) {
 *   updateTheme(theme);
 *   saveThemeToStorage(theme);
 * }
 */
export const loadThemeFromAPI = async (url) => {
  try {
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const theme = await response.json();
    
    // Validate before returning
    if (validateTheme(theme)) {
      return theme;
    } else {
      console.error('Invalid theme received from API');
      return null;
    }
  } catch (error) {
    console.error('Error loading theme from API:', error);
    return null;
  }
};

/**
 * Initialize theme system with support for external sources
 * Checks localStorage first, then falls back to default theme
 * 
 * @param {ThemeConfig} [defaultTheme=THEME] - Default theme to use if no saved theme found
 * @returns {ThemeConfig} The theme that was applied
 * 
 * @example
 * // In your app initialization
 * const appliedTheme = initializeTheme(THEME);
 */
export const initializeTheme = (defaultTheme = THEME) => {
  // Try to load from localStorage first
  const savedTheme = loadThemeFromStorage();
  const themeToApply = savedTheme || defaultTheme;
  
  // Apply the theme
  applyThemeVariables(themeToApply);
  
  // Store as current theme
  window.__CURRENT_THEME__ = themeToApply;
  
  return themeToApply;
};

/**
 * Validate theme configuration
 * Checks that all required fields are present and properly formatted
 * 
 * @param {ThemeConfig} theme - Theme configuration to validate
 * @returns {boolean} True if valid, false otherwise
 * 
 * @example
 * if (validateTheme(customTheme)) {
 *   applyThemeVariables(customTheme);
 * }
 */
export const validateTheme = (theme) => {
  const errors = [];
  
  // Check required color properties
  const requiredColors = [
    "primary", "secondary", "headerBg", "sidebarBg",
    "textPrimary", "background", "border", "success", "danger", "white"
  ];
  
  requiredColors.forEach((color) => {
    if (!theme.colors?.[color]) {
      errors.push(`Missing required color: ${color}`);
    } else if (!/^#[0-9A-Fa-f]{3,6}$/.test(theme.colors[color])) {
      errors.push(`Invalid color format for ${color}: ${theme.colors[color]}`);
    }
  });
  
  // Check typography
  if (!theme.typography?.fontFamily) {
    errors.push("Missing required typography.fontFamily");
  }
  
  if (errors.length > 0) {
    console.error("Theme validation errors:", errors);
    return false;
  }
  
  return true;
};
